// Offline POS line resolution + sale capture (P9.3). Online path delegates to existing endpoints;
// offline path prices from cached base + GST + manual discount and tags priced_offline:true.
import { useCallback } from "react";
import { api } from "@/lib/api";
import { findByCode, enqueueSale } from "@/lib/offlineDb";

const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `txn_${Date.now()}_${Math.random().toString(36).slice(2)}`);

export function useOfflinePos(online) {
  // Resolve a scanned/typed code to a priced line.
  const resolve = useCallback(async (code, qty = 1) => {
    if (online) {
      try {
        const { data } = await api.get(`/pos/scan/resolve?code=${encodeURIComponent(code)}`);
        return { ...(data?.data || data), priced_offline: false };
      } catch (_) { /* fall through to offline */ }
    }
    const p = await findByCode(code);
    if (!p) return null;
    const unit = Number(p.unit_price || 0);
    const net = +(unit * qty).toFixed(2);
    const gst = +((net * Number(p.gst_pct || 0)) / 100).toFixed(2);
    return {
      product_id: p.product_id, sku: p.sku, name: p.name, category: p.category,
      qty, unit_price: unit, gst_pct: p.gst_pct, net, gst, total: +(net + gst).toFixed(2),
      priced_offline: true,
    };
  }, [online]);

  // Complete a sale. Online → existing checkout (caller handles). Offline → durable enqueue.
  const completeSaleOffline = useCallback(async ({ lines, tenders = [], customer_name = "Walk-in", discount = 0, channel = "Web POS" }) => {
    const subtotal = +lines.reduce((s, l) => s + (l.net || 0), 0).toFixed(2);
    const taxTotal = +lines.reduce((s, l) => s + (l.gst || 0), 0).toFixed(2);
    const total = +(subtotal - (discount || 0) + taxTotal).toFixed(2);
    const client_txn_id = uuid();
    const payload = {
      channel, customer_name,
      lines: lines.map((l) => ({ sku: l.sku, name: l.name, qty: l.qty, unit_price: l.unit_price,
        gst_pct: l.gst_pct, net: l.net, gst: l.gst, priced_offline: true })),
      subtotal, total_discount: discount || 0, tax: { total: taxTotal }, total,
      paid: tenders.reduce((s, t) => s + (t.amount || 0), 0) || total, tenders,
      priced_offline: true, client_created_at: new Date().toISOString(),
    };
    await enqueueSale({ client_txn_id, payload });
    return {
      client_txn_id, status: "queued_offline", total,
      receipt: { number: `OFFLINE-${client_txn_id.slice(0, 8).toUpperCase()}`, lines: payload.lines, total, priced_offline: true },
    };
  }, []);

  return { resolve, completeSaleOffline };
}

// Features that cannot run offline (no live server) — callers grey these out.
export const OFFLINE_DISABLED = ["promotions", "coupons", "loyalty", "e_invoice", "digital_receipt"];
export const isFeatureOffline = (online, feature) => !online && OFFLINE_DISABLED.includes(feature);
